import { ApolloError, useMutation } from '@apollo/client';
import { Button, Icon, SettingsGroup, SettingsItemCheckbox, Tooltip } from '@elipssolution/harfang';
import { mdiAlertCircle, mdiPencil, mdiPlus } from '@mdi/js';
import { Typography, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';

import DomainNameField from './DomainNameField';
import ModulesAvailabilityTable from './ModulesAvailabilityTable';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import PermissionWall from '../../../../components/PermissionWall';
import SettingsDialogPage from '../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { DomainType } from '../../../../types/domain';
import { PermissionEnum } from '../../../../types/permission';
import { generateErrorInformations } from '../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../utils/statusAnimationDelay';
import { CREATE_DOMAIN, CreateDomainType, REMOVE_DOMAIN, UPDATE_DOMAIN, UpdateDomainType } from '../../../api/domain';

const ErrorWrapper = styled('div')(({ theme }) => ({
	overflow: 'auto',

	minHeight: 100,
	minWidth: '50%',
	maxWidth: '75%',

	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',

	padding: theme.spacing(1),
	gap: theme.spacing(1),
	margin: theme.spacing(2, 'auto'),

	color: theme.palette.error.main,
	backgroundColor: `${theme.palette.error.main}1A`,
	borderRadius: theme.shape.borderRadius,
}));

const ButtonWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

type FormType = {
	isDefault: DomainType['isDefault'];
	name: DomainType['name'];
};

type DomainProps = {
	domain?: DomainType;
};

const DomainForm = ({ domain: initialDomain }: DomainProps) => {
	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		clearErrors,
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		reset,
		setError,
	} = useForm<FormType>();

	const [domain, setDomain] = useState(initialDomain);

	const { id, isDefault, name } = domain ?? {};

	const [errorMessage, setErrorMessage] = useState('');
	const [isCreateDomainSucceeded, setIsCreateDomainSucceeded] = useState(false);
	const [isUpdateDomainSucceeded, setIsUpdateDomainSucceeded] = useState(false);

	const [createDomain, { loading: isCreateDomainLoading }] = useMutation<CreateDomainType>(CREATE_DOMAIN, {
		onCompleted: ({ createDomain: domainResult }) => {
			setDomain(domainResult);
			setIsCreateDomainSucceeded(true);
			setTimeout(() => setIsCreateDomainSucceeded(false), SUCCESS_ANIMATION_DELAY);
		},
	});

	const [updateDomain, { loading: isUpdateDomainLoading }] = useMutation<UpdateDomainType>(UPDATE_DOMAIN, {
		onCompleted: ({ updateDomain: domainResult }) => {
			setDomain(domainResult);
			setIsUpdateDomainSucceeded(true);
			setTimeout(() => setIsUpdateDomainSucceeded(false), SUCCESS_ANIMATION_DELAY);
		},
	});

	const [removeDomain, { loading: isRemoveDomainLoading }] = useMutation(REMOVE_DOMAIN, {
		onCompleted: back,
		onError: (error: ApolloError) =>
			setErrorMessage(generateErrorInformations({ error, resource: 'removeDomain' })?.message),
	});

	const isLoading = useMemo(
		() => isCreateDomainLoading || isUpdateDomainLoading,
		[isCreateDomainLoading, isUpdateDomainLoading],
	);

	const isMutationButtonDisabled = useMemo(
		() => isRemoveDomainLoading || !isValid || !isDirty,
		[isDirty, isRemoveDomainLoading, isValid],
	);

	const startIcon = useMemo(() => <Icon path={domain ? mdiPencil : mdiPlus} />, [domain]);

	const onSubmit = useCallback(
		(values: FieldValues) =>
			(domain
				? updateDomain({
						variables: {
							updateDomainInput: {
								id: domain.id,
								...values,
							},
						},
				  })
				: createDomain({
						variables: {
							createDomainInput: {
								...values,
							},
						},
				  })
			).catch((error: ApolloError) =>
				setErrorMessage(
					generateErrorInformations({ error, resource: domain ? 'updateDomain' : 'createDomain' })?.message,
				),
			),
		[domain, updateDomain, createDomain],
	);

	const handleDomainNameError = useCallback(
		(error?: string) => (error ? setError('name', { message: error }) : clearErrors('name')),
		[clearErrors, setError],
	);

	const submitButtonLabel = useMemo(() => {
		if (domain) {
			if (isCreateDomainSucceeded) return 'Domaine ajouté';
			if (isUpdateDomainSucceeded) return 'Domaine modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isCreateDomainSucceeded, isUpdateDomainSucceeded, domain]);

	const handleDomainRemoveButtonClick = async () => {
		if (!name) return undefined;

		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer le domaine ${name} ?`,
			content: 'Les dossiers attachés à ce domaine seront attachés au domaine par défaut.',
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		return removeDomain({
			variables: {
				removeDomainId: id,
			},
		});
	};

	useEffect(() => {
		reset({ isDefault, name });
	}, [domain, isDefault, name, reset]);

	useEffect(() => {
		reset({ isDefault, name });
	}, [domain, isDefault, name, reset]);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${initialDomain ? 'modification' : 'création'} du domaine ?`,
		);
	}, [isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange, initialDomain]);

	return (
		<SettingsDialogPage title={name ?? "Ajout d'un domaine"}>
			<SettingsGroup>
				<DomainNameField initialDomainName={name} control={control} onError={handleDomainNameError} />

				<Controller
					name="isDefault"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le domaine est celui par défaut."
							disabled={isDefault}
							disabledTooltipContent={isDefault ? 'Vous ne pouvez pas modifier le domaine par défaut.' : undefined}
							label="Défaut"
						/>
					)}
				/>
			</SettingsGroup>

			<ButtonWrapper>
				<Button
					disabled={isMutationButtonDisabled}
					onClick={handleSubmit(onSubmit)}
					startIcon={startIcon}
					variant="contained"
				>
					{submitButtonLabel}
				</Button>

				{domain && (
					<Tooltip
						content={
							isDefault ? 'Vous ne pouvez pas supprimer un domaine configuré comme domaine par défaut' : undefined
						}
					>
						<Button
							color="error"
							disabled={isDefault || isLoading}
							onClick={handleDomainRemoveButtonClick}
							variant="outlined"
						>
							Supprimer
						</Button>
					</Tooltip>
				)}
			</ButtonWrapper>

			{id && (
				<PermissionWall
					permissionCodes={[
						PermissionEnum.DOCUMENT_SETTING_STORAGE,
						PermissionEnum.DOCUMENT_SETTING_TAG,
						PermissionEnum.QUICKENTRY_SETTING,
						PermissionEnum.ACCOUNTING_SETTING,
						PermissionEnum.SIGN_ACCESS,
					]}
				>
					<ModulesAvailabilityTable domainId={id} onError={setErrorMessage} />
				</PermissionWall>
			)}

			{errorMessage && (
				<ErrorWrapper>
					<Icon path={mdiAlertCircle} />
					<Typography>{errorMessage}</Typography>
				</ErrorWrapper>
			)}
		</SettingsDialogPage>
	);
};

export default DomainForm;
